import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * Sitewide LocalBusiness / MedicalBusiness JSON-LD, injected via Helmet.
 * Suppressed on the 7 SEO landing pages because each of those routes
 * injects its own page-specific LocalBusiness schema (with hasOfferCatalog
 * targeting that service) from pageSchemas.js.
 *
 * Keeping only one LocalBusiness per page avoids duplicate @id ambiguity.
 */
const SEO_LANDING_SLUGS = new Set([
    "/facial-services-san-diego-ca",
    "/microneedling-san-diego-ca",
    "/neurotoxins-san-diego-ca",
    "/im-shots-san-diego-ca",
    "/iv-therapy-san-diego-ca",
    "/medical-weight-loss-san-diego-ca",
    "/peptide-therapy-san-diego-ca",
]);

const SITEWIDE_LOCAL_BUSINESS = {
    "@context": "https://schema.org",
    "@type": ["HealthAndBeautyBusiness", "MedicalBusiness"],
    "@id": "https://zenithwellnesss.com/#localbusiness",
    name: "Zenith Wellness",
    url: "https://zenithwellnesss.com/",
    logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/pq79qyzz_84d55109-d543-4a1a-9205-51b665e4d9d0.webp",
    },
    image: "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/pq79qyzz_84d55109-d543-4a1a-9205-51b665e4d9d0.webp",
    description:
        "Zenith Wellness is a San Diego med spa offering clinician-led, medical-grade treatments — facials, IV therapy, IM shots, neurotoxins, microneedling, medical weight loss and peptide therapy — for Mission Valley, Hillcrest, North Park, La Jolla, Kensington and Downtown San Diego.",
    telephone: "+1-858-905-4117",
    email: "info@zenithwellness-sd.com",
    priceRange: "$$",
    address: {
        "@type": "PostalAddress",
        streetAddress: "3727 Camino Del Rio S, Suite 210",
        addressLocality: "San Diego",
        addressRegion: "CA",
        postalCode: "92108",
        addressCountry: "US",
    },
    geo: { "@type": "GeoCoordinates", latitude: 32.7774515, longitude: -117.1144108 },
    hasMap:
        "https://www.google.com/maps/place/Zenith+Wellness+Aesthetics+%26+Med+Spa/@32.7774515,-117.1144108,811m/data=!3m2!1e3!4b1!4m6!3m5!1s0x80d95589d34a6d75:0x86e37ae5f83b0b2c!8m2!3d32.7774515!4d-117.1144108!16s%2Fg%2F11ywxpmjxz!5m1!1e2",
    openingHoursSpecification: [
        {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Wednesday", "Thursday", "Saturday"],
            opens: "09:00",
            closes: "15:00",
        },
    ],
    areaServed: [
        {
            "@type": "City",
            "@id": "https://en.wikipedia.org/wiki/San_Diego",
            name: "San Diego",
            sameAs: "https://en.wikipedia.org/wiki/San_Diego",
        },
        { "@type": "Place", name: "Mission Valley, San Diego, CA" },
        { "@type": "Place", name: "Hillcrest, San Diego, CA" },
        { "@type": "Place", name: "North Park, San Diego, CA" },
        { "@type": "Place", name: "La Jolla, San Diego, CA" },
        { "@type": "Place", name: "Kensington, San Diego, CA" },
        { "@type": "Place", name: "Downtown San Diego, CA" },
    ],
    medicalSpecialty: [
        "Aesthetic Medicine",
        "Weight Management",
        "IV Therapy",
        "Peptide Therapy",
        "Hair Restoration",
    ],
    sameAs: [
        "https://www.instagram.com/zenithwellness_sd/",
        "https://www.facebook.com/people/Zenith-Wellness/61587655520941/",
        "https://maps.app.goo.gl/ZCuN1HaT3j3uscjj9",
        "https://www.yelp.com/biz/zenith-wellness-san-diego",
        "https://www.mypeptidematch.com/clinic/zenith-wellness-san-diego-ca",
        "https://www.mapquest.com/us/california/zenith-wellness-796284459",
        "https://www.glowlocator.com/clinic/zenith-wellness-in-san-diego",
        "https://www.tiktok.com/@zenith_wellness0",
        "https://reviews.birdeye.com/zenith-wellness-177455245741755",
        "https://zenithwellness.setmore.com/",
        "https://peptidesnear.me/listing/zenith-wellness/",
        "https://solo.to/zenithwellness",
        "https://www.medicalspalocator.com/med-spa/zenith-wellness-san-diego-ca",
    ],
};

export default function GlobalLocalBusiness() {
    const location = useLocation();
    if (SEO_LANDING_SLUGS.has(location.pathname)) return null;
    return (
        <Helmet>
            <script type="application/ld+json">{JSON.stringify(SITEWIDE_LOCAL_BUSINESS)}</script>
        </Helmet>
    );
}
